import React, { useState } from 'react';
import { Box, Button, Collapse, Group, Stack, Text, Textarea, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconMessageCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Goal } from '../../types';
import { useAddGoalComment } from '../../hooks/useGoalApi';
import { formatDateTime } from '../../utils/date';
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/api';

// Лента обсуждения цели. Свёрнута по умолчанию: в списке целей важны статус и
// срок, а разговор открывают, когда к нему возвращаются.
export const GoalComments: React.FC<{ goal: Goal }> = ({ goal }) => {
  const { t, i18n } = useTranslation();
  const addComment = useAddGoalComment();
  const comments = goal.comments ?? [];
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');

  const send = async () => {
    const value = text.trim();
    if (!value) return;
    try {
      await addComment.mutateAsync({ goalId: goal._id, text: value });
      setText('');
    } catch (e) {
      showToast({
        title: t('goals.commentFailed'),
        message: getErrorMessage(e, ''),
        type: 'error',
      });
    }
  };

  return (
    <Box>
      <UnstyledButton onClick={() => setIsOpen((v) => !v)} aria-expanded={isOpen}>
        <Group gap={6} c="dimmed">
          <IconMessageCircle size={14} />
          <Text size="xs">
            {comments.length
              ? t('goals.commentsCount', { count: comments.length })
              : t('goals.comments')}
          </Text>
          <IconChevronDown
            size={12}
            style={{
              transform: isOpen ? 'rotate(180deg)' : undefined,
              transition: 'transform 150ms',
            }}
          />
        </Group>
      </UnstyledButton>
      <Collapse in={isOpen}>
        <Stack gap="xs" mt="xs">
          {comments.length === 0 && (
            <Text size="xs" c="dimmed">
              {t('goals.commentsEmpty')}
            </Text>
          )}
          {comments.map((c) => (
            <Box key={c._id}>
              <Group gap={6}>
                <Text size="xs" fw={500}>
                  {c.author?.name ?? '—'}
                </Text>
                <Text size="xs" c="dimmed">
                  {formatDateTime(c.createdAt, i18n.language)}
                </Text>
              </Group>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {c.text}
              </Text>
            </Box>
          ))}
          <Textarea
            aria-label={t('goals.comments')}
            placeholder={t('goals.commentPlaceholder')}
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            maxLength={2000}
            autosize
            minRows={1}
            maxRows={6}
          />
          <Group justify="flex-end">
            <Button
              size="xs"
              variant="light"
              onClick={send}
              loading={addComment.isPending}
              disabled={!text.trim()}
            >
              {t('goals.commentSend')}
            </Button>
          </Group>
        </Stack>
      </Collapse>
    </Box>
  );
};
